import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, buildCombinedReason } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

const ARTERY_LABELS = {
  subclavianArtery: 'Subclavian Artery',
  axillaryArtery: 'Axillary Artery',
  brachialArtery: 'Brachial Artery',
  radialArtery: 'Radial Artery',
  ulnarArtery: 'Ulnar Artery'
};

function buildArmSummaryLines(armData, evaluated) {
  if (!evaluated) return [{ text: 'Not evaluated', tone: 'neutral' }];
  return Object.entries(ARTERY_LABELS).map(([key, label]) => {
    const artery = armData[key] || {};
    const parts = [];
    if (artery.psv) parts.push(`${artery.psv} cm/s`);
    if (artery.waveform) parts.push(artery.waveform);
    const tone = (artery.waveform === 'Monophasic' || artery.waveform === 'Not Well Visualized') ? 'abnormal' : 'normal';
    return { text: `${label}: ${parts.join(', ') || 'Not recorded'}`, tone };
  });
}

export function UpperExtremityArterialPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const ra = data.rightArm || {};
  const la = data.leftArm || {};
  const rightSummary = buildArmSummaryLines(ra, data.rightArmEvaluated);
  const leftSummary = buildArmSummaryLines(la, data.leftArmEvaluated);

  const toneColor = (tone) => tone === 'abnormal' ? COLORS.abnormal : tone === 'normal' ? COLORS.normal : COLORS.black;

  const renderArteryDetail = (armData, evaluated) => {
    if (!evaluated) return <PdfLabelValue label="Status:" value="Not evaluated" forceBlack />;
    return Object.entries(ARTERY_LABELS).map(([key, label]) => {
      const artery = armData[key] || {};
      const isAbnormal = artery.waveform === 'Monophasic' || artery.waveform === 'Not Well Visualized';
      const value = [artery.psv ? `${artery.psv} cm/s` : null, artery.waveform || null].filter(Boolean).join(', ');
      return (
        <View key={key}>
          <PdfLabelValue label={`${label}:`} value={value || 'Not recorded'} isAbnormal={isAbnormal} />
          {artery.notes && (
            <View style={{ paddingLeft: 10 }}>
              <PdfLabelValue label="Notes:" value={artery.notes} isAbnormal={isAbnormal} />
            </View>
          )}
        </View>
      );
    });
  };

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="UPPER EXTREMITY ARTERIAL ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfMeasurementBox title="FINDINGS SUMMARY">
              <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>Right Arm:</Text>
              {rightSummary.map((line, i) => (
                <Text key={i} style={[commonStyles.infoBoxText, { color: toneColor(line.tone) }]}>{line.text}</Text>
              ))}
              <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginTop: 6, marginBottom: 4 }]}>Left Arm:</Text>
              {leftSummary.map((line, i) => (
                <Text key={i} style={[commonStyles.infoBoxText, { color: toneColor(line.tone) }]}>{line.text}</Text>
              ))}
            </PdfMeasurementBox>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            <PdfAccentSectionHeader title="RIGHT UPPER EXTREMITY" />
            {renderArteryDetail(ra, data.rightArmEvaluated)}

            <PdfAccentSectionHeader title="LEFT UPPER EXTREMITY" />
            {renderArteryDetail(la, data.leftArmEvaluated)}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
